import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FaUserCircle, FaSignOutAlt, FaBookmark, FaTags, FaRobot, FaComments, FaBrain, FaExchangeAlt, FaSearch } from 'react-icons/fa';
import { PAPER_SOURCE, SOURCE_NAME } from '../constants/tagConstants';
import PaperLookup from './PaperLookup';

function Header() {
  const { user, loading, signOut, eligibility } = useAuth();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [showLookup, setShowLookup] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      setIsLoggedIn(user !== null);
    }
  }, [user, loading]);

  useEffect(() => {
    setShowMenu(false);
    setShowSourceMenu(false);
    setShowLookup(false);
  }, [location]);

  // Global Ctrl+K / Cmd+K shortcut to open paper lookup
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowLookup((v) => !v);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowMenu(false);
      setIsLoggedIn(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const renderUsageMetrics = () => {
    if (!user) return null;

    // Four metrics now — the original three plus AI chat. A 2×2 grid fits
    // the dropdown width comfortably where a 4-column row would cramp the
    // "Summaries" label.
    const fmt = (q) => (q === Infinity ? '∞' : q);
    const metrics = [
      { icon: FaBookmark, label: 'Papers', current: eligibility.favoritePaperUsage || 0, max: fmt(eligibility.favoritePaperQuota) },
      { icon: FaTags, label: 'Tags', current: eligibility.favoriteTagUsage || 0, max: fmt(eligibility.favoriteTagQuota) },
      { icon: FaRobot, label: 'Summaries', current: eligibility.aiSummaryUsage || 0, max: fmt(eligibility.aiSummaryQuota) },
      { icon: FaComments, label: 'Chats', current: eligibility.aiChatUsage || 0, max: fmt(eligibility.aiChatQuota) },
    ];

    return (
      <div className="px-4 py-3 border-t border-gray-100/80">
        <div className="grid grid-cols-2 gap-3">
          {metrics.map((metric, index) => (
            <div key={index} className="text-center">
              <metric.icon className="mx-auto mb-1 text-[10px] text-gray-300" />
              <div className="text-[11px] font-bold text-gray-800 tabular-nums">
                {metric.current}/{metric.max}
              </div>
              <div className="text-[9px] text-gray-400 uppercase tracking-wider">{metric.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Build URLs to other source subdomains.
  // Production: bio.paperdigest.xyz, psy.paperdigest.xyz, paperdigest.xyz
  // Staging:    dev.bio.paperdigest.xyz, dev.psy.paperdigest.xyz, dev.paperdigest.xyz
  // Development: different local ports
  const sourceUrl = (source) => {
    if (typeof window === 'undefined') return '#';
    const { protocol, hostname, port } = window.location;
    const path = location.pathname + location.search;

    // Development: map source to local port
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const portMap = { arxiv: '4000', biorxiv: '4010', psyarxiv: '4020' };
      return `${protocol}//${hostname}:${portMap[source] || port}${path}`;
    }

    // Production / staging: swap the subdomain, preserving dev. prefix
    const isStaging = hostname.startsWith('dev.');
    const baseDomain = hostname.replace(/^(dev\.)?(bio\.|psy\.)?/, '');
    const sourcePrefix = { arxiv: '', biorxiv: 'bio.', psyarxiv: 'psy.' }[source] || '';
    const devPrefix = isStaging ? 'dev.' : '';
    return `${protocol}//${devPrefix}${sourcePrefix}${baseDomain}${path}`;
  };

  const SOURCES = [
    { key: 'arxiv', name: 'arXiv', description: 'Physics, Math, CS & more', color: 'text-red-400', bg: 'bg-red-500/10', activeBg: 'bg-red-500/20', border: 'border-red-500/20' },
    { key: 'biorxiv', name: 'bioRxiv', description: 'Biology preprints', color: 'text-green-400', bg: 'bg-green-500/10', activeBg: 'bg-green-500/20', border: 'border-green-500/20' },
    { key: 'psyarxiv', name: 'psyArXiv', description: 'Psychology preprints', color: 'text-blue-400', bg: 'bg-blue-500/10', activeBg: 'bg-blue-500/20', border: 'border-blue-500/20' },
  ];

  const currentSource = SOURCES.find((s) => s.key === PAPER_SOURCE) || SOURCES[0];

  if (loading) return null;

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Today', path: '/today' },
    { name: 'Archive', path: '/archive' },
    { name: 'AI Digest', path: '/ai' },
    { name: 'Pricing', path: '/pricing' },
  ];

  return (
    <motion.header
      className="sticky top-0 z-50 glass-nav border-b border-white/10"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 100, damping: 20, delay: 0.1 }}
    >
      <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between h-14 sm:h-16">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0 group">
          <motion.div className="flex items-center gap-2" whileHover={{ x: 2 }}>
            <svg viewBox="0 0 22 28" className="w-5 h-6 sm:w-6 sm:h-7 opacity-70 group-hover:opacity-100 transition-opacity" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1h14l5 5v21H1V1z" stroke="white" strokeWidth="1.2" strokeLinejoin="round" opacity="0.25"/>
              <path d="M15 1v5h5" stroke="white" strokeWidth="1.2" strokeLinejoin="round" opacity="0.15"/>
              <line x1="4" y1="8" x2="16" y2="8" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.18"/>
              <line x1="4" y1="11" x2="13" y2="11" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.12"/>
              <line x1="4" y1="14" x2="10" y2="14" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.07"/>
              <g stroke="#FDE047" strokeWidth="0.6" opacity="0.3">
                <line x1="6" y1="19" x2="11" y2="20.5"/>
                <line x1="11" y1="20.5" x2="16" y2="18.5"/>
                <line x1="11" y1="20.5" x2="8" y2="24"/>
                <line x1="11" y1="20.5" x2="14.5" y2="24"/>
                <line x1="8" y1="24" x2="14.5" y2="24"/>
              </g>
              <circle cx="6" cy="19" r="1.4" fill="#FDE047"/>
              <circle cx="11" cy="20.5" r="1.8" fill="#FDE047"/>
              <circle cx="16" cy="18.5" r="1.2" fill="#FDE047"/>
              <circle cx="8" cy="24" r="1.4" fill="#FDE047"/>
              <circle cx="14.5" cy="24" r="1.2" fill="#FDE047"/>
            </svg>
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              paper<span className="text-yellow-300">digest</span>
            </h1>
          </motion.div>
        </Link>

        <div className="flex items-center">
          {/* Source Switcher */}
          <div className="relative mr-2 sm:mr-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowSourceMenu(!showSourceMenu)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-bold tracking-wide transition-all duration-200 border-2 ${currentSource.border} ${currentSource.bg} hover:${currentSource.activeBg} hover:border-opacity-60`}
            >
              <div className={`w-2 h-2 rounded-full ${
                PAPER_SOURCE === 'arxiv' ? 'bg-red-400' :
                PAPER_SOURCE === 'biorxiv' ? 'bg-green-400' : 'bg-blue-400'
              } shadow-sm`} />
              <span className={`${currentSource.color} font-extrabold`}>{SOURCE_NAME}</span>
              <FaExchangeAlt className="w-3 h-3 text-white/40" />
            </motion.button>

            <AnimatePresence>
              {showSourceMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowSourceMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.92, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: -4 }}
                    transition={{ duration: 0.12, ease: 'easeOut' }}
                    className="absolute left-0 mt-2 w-52 bg-white rounded-xl shadow-card-hover py-1.5 z-20 overflow-hidden border border-gray-100"
                  >
                    <div className="px-3 pt-1.5 pb-2">
                      <p className="text-[9px] text-gray-400 uppercase tracking-widest font-semibold">Switch source</p>
                    </div>
                    {SOURCES.map((source) => {
                      const isCurrent = source.key === PAPER_SOURCE;
                      return (
                        <a
                          key={source.key}
                          href={isCurrent ? undefined : sourceUrl(source.key)}
                          onClick={() => setShowSourceMenu(false)}
                          className={`flex items-center px-3 py-2.5 transition-colors ${
                            isCurrent
                              ? 'bg-gray-50 cursor-default'
                              : 'hover:bg-gray-50 cursor-pointer'
                          }`}
                        >
                          <div className={`w-2 h-2 rounded-full mr-2.5 flex-shrink-0 ${
                            source.key === 'arxiv' ? 'bg-red-400' :
                            source.key === 'biorxiv' ? 'bg-green-400' : 'bg-blue-400'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <div className={`text-xs font-semibold ${isCurrent ? 'text-gray-900' : 'text-gray-700'}`}>
                              {source.name}
                              {isCurrent && (
                                <span className="ml-1.5 text-[9px] font-bold text-purple-500 uppercase">current</span>
                              )}
                            </div>
                            <div className="text-[10px] text-gray-400">{source.description}</div>
                          </div>
                        </a>
                      );
                    })}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Desktop Nav - hidden on mobile (bottom nav handles it) */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = item.path === '/'
                ? location.pathname === '/'
                : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className="relative px-3.5 py-2 text-sm rounded-md transition-colors duration-200"
                >
                  <span className={`relative z-10 ${isActive ? 'text-white font-semibold' : 'text-white/50 hover:text-white/90'}`}>
                    {item.name}
                  </span>
                  {isActive && (
                    <motion.div
                      className="absolute inset-0 bg-white/10 rounded-md border border-white/10"
                      layoutId="activeNav"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Separator - desktop only */}
          <div className="hidden md:block w-px h-5 bg-white/15 mx-3" />

          {/* Paper lookup / search button - visible on all screen sizes */}
          <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            onClick={() => setShowLookup(true)}
            className="flex items-center justify-center w-8 h-8 rounded-md bg-white/10 hover:bg-white/20 transition-colors duration-200 border border-white/10 mr-2"
            title="Find or fetch a paper — ID/URL or keywords (⌘K)"
            aria-label="Open paper search: fetch by ID or URL, or search by keywords"
          >
            <FaSearch className="w-3 h-3 text-white/70" />
          </motion.button>

          {/* User controls - visible on all screen sizes */}
          <div className="relative">
            {isLoggedIn ? (
              <motion.button
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center justify-center w-8 h-8 rounded-md bg-white/10 hover:bg-white/20 transition-colors duration-200 border border-white/10"
              >
                <FaUserCircle className="w-4 h-4 text-white/80" />
              </motion.button>
            ) : (
              <Link to="/sign-in" className="inline-flex">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="bg-yellow-300 text-gray-900 px-4 sm:px-3.5 py-2.5 sm:py-1.5 rounded-md text-sm sm:text-xs font-bold hover:bg-yellow-200 transition-all duration-200 tracking-wide uppercase min-h-[40px] sm:min-h-0"
                >
                  Sign In
                </motion.button>
              </Link>
            )}

            <AnimatePresence>
              {showMenu && isLoggedIn && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.92, y: -6 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: -6 }}
                    transition={{ duration: 0.12, ease: 'easeOut' }}
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-card-hover py-1 z-20 overflow-hidden border border-gray-100"
                  >
                    <Link
                      to="/user"
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setShowMenu(false)}
                    >
                      <div className="w-8 h-8 rounded-md bg-purple-50 flex items-center justify-center mr-3">
                        <FaUserCircle className="text-purple-400 text-xs" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 text-xs truncate">{user?.name || 'Profile'}</div>
                        <div className="text-[10px] text-gray-400 truncate">{user?.email}</div>
                      </div>
                    </Link>
                    <Link
                      to="/ai"
                      className="flex items-center px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100/80"
                      onClick={() => setShowMenu(false)}
                    >
                      <div className="w-6 h-6 rounded-md bg-purple-50 flex items-center justify-center mr-2.5">
                        <FaBrain className="text-purple-400 text-[10px]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 text-[11px]">AI Digest</div>
                        <div className="text-[10px] text-gray-400">Summaries &amp; chats</div>
                      </div>
                    </Link>
                    {renderUsageMetrics()}
                    <div className="border-t border-gray-100">
                      <button
                        onClick={handleSignOut}
                        className="flex items-center w-full px-4 py-2.5 text-xs text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <FaSignOutAlt className="mr-2 text-[10px]" />
                        Sign Out
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      <PaperLookup isOpen={showLookup} onClose={() => setShowLookup(false)} />
    </motion.header>
  );
}

export default Header;
